'use client';

import { useCallback, useEffect, useState } from 'react';

/**
 * Safety cap on any list query.
 *
 * Every list*() data-access function used to SELECT the entire table. On a
 * young account that is fine, and it is why the UI worked; on an account with
 * tens of thousands of stones it is a page that can take the database down and
 * a browser that cannot paint. This bound is what stops that, and
 * scripts/perf-audit.cjs fails the build if a list query loses its limit.
 *
 * Deliberately generous so existing client-side pagination keeps working.
 * The follow-up is to make those screens paginate server-side and read the
 * total count rather than slicing an array in the browser.
 */
export const DEFAULT_PAGE_SIZE = 500;

/**
 * Shared list-loading state for entity modules.
 * Owns: initial load, error surface, and optimistic upsert/remove helpers.
 * The loader must be a stable reference (module-level data-layer function).
 */
export function useEntityList<T extends { id: string }>(loader: () => Promise<T[]>) {
  const [items, setItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const data = await loader();
      setItems(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [loader]);

  useEffect(() => {
    reload();
  }, [reload]);

  /** Insert or replace by id (optimistic update after a successful write). */
  const upsert = useCallback((item: T) => {
    setItems((prev) => {
      const i = prev.findIndex((x) => x.id === item.id);
      if (i === -1) return [item, ...prev];
      const next = prev.slice();
      next[i] = item;
      return next;
    });
  }, []);

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  return { items, setItems, isLoading, error, reload, upsert, remove };
}

/** Unwrap a supabase PostgrestResult, throwing on error so callers can toast. */
export function orThrow<T>(data: T | null, error: { message: string } | null): T {
  if (error) throw new Error(error.message);
  return data as T;
}

/** Generate a client-side id for TEXT-primary-key tables. */
export function newId(prefix: string): string {
  const uuid =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${uuid}`;
}

export function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Normalize empty strings to null for optional DB text/date columns. */
export function emptyToNull<T extends Record<string, unknown>>(obj: T, keys: (keyof T)[]): T {
  const out = { ...obj };
  for (const key of keys) {
    if (out[key] === '') (out as Record<string, unknown>)[key as string] = null;
  }
  return out;
}
